import {
    Component,
    EventEmitter,
    forwardRef,
    inject,
    Input,
    OnChanges,
    Output,
    signal,
    SimpleChanges,
    ViewEncapsulation,
} from "@angular/core";
import { TranslatePipe, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "./i18n/scope";
import { Subject } from "rxjs";
import { CerrixButtonComponent } from "@app/shared/atoms/cerrix-button/cerrix-button.component";
import { CerrixIconButtonComponent } from "@app/shared/atoms/cerrix-icon-button/cerrix-icon-button.component";
import { CerrixLabelComponent } from "@app/shared/atoms/cerrix-label/cerrix-label.component";
import { CerrixInstructionsIconComponent } from "@app/shared/cerrix-instructions-icon/cerrix-instructions-icon.component";
import { CerrixInputValidationErrorsDirective } from "@app/shared/directives/cerrix-input-fields/cerrix-input-validation-errors.directive";
import {
    CERRIX_INPUT_VALUE_SOURCE,
    CerrixInputValueSource,
} from "@app/shared/directives/cerrix-input-fields/models/cerrix-input-value-source.interface";
import { CerrixAvatarGroupComponent } from "@app/shared/molecules/cerrix-avatar-group/cerrix-avatar-group.component";
import { CerrixDialogService } from "@app/shared/organisms/cerrix-dialog/cerrix-dialog-service.component";
import { CerrixDialogComponent } from "@app/shared/organisms/cerrix-dialog/cerrix-dialog.component";
import { CerrixUserTypePickerComponent } from "@app/shared/organisms/cerrix-user-type-picker/cerrix-user-type-picker.component";
import { CerrixUserTypePickerConfig } from "@app/shared/organisms/cerrix-user-type-picker/models/cerrix-user-type-picker-config";

/**
 * A field that shows the users picked for one or more types (owner, responsible, ...) as a group
 * of avatars, and lets the user change them in a dialog. The dialog edits a draft: it is handed
 * over through valueChange only when the user confirms, and discarded when they cancel.
 *
 * The caller decides what is picked, through `types`. A single type gives a plain user picker.
 * It does not load users, so the caller passes them in per type.
 */
@Component({
    selector: "app-cerrix-user-select",
    templateUrl: "./cerrix-user-select.component.html",
    styleUrls: ["./cerrix-user-select.component.scss"],
    imports: [
        TranslatePipe,
        CerrixLabelComponent,
        CerrixInstructionsIconComponent,
        CerrixAvatarGroupComponent,
        CerrixButtonComponent,
        CerrixIconButtonComponent,
        CerrixDialogComponent,
        CerrixUserTypePickerComponent,
        CerrixInputValidationErrorsDirective,
    ],
    viewProviders: [provideTranslationScope(scope)],
    providers: [
        {
            provide: CERRIX_INPUT_VALUE_SOURCE,
            useExisting: forwardRef(() => CerrixUserSelectComponent),
        },
    ],
    encapsulation: ViewEncapsulation.None,
})
export class CerrixUserSelectComponent implements OnChanges, CerrixInputValueSource<string[]> {
    @Input() name = "user-select";
    @Input() types: CerrixUserTypePickerConfig[] = [];
    @Input() value: Record<string, string[]> = {};
    @Input() readonly = false;

    @Input() label: string;
    @Input() infoText = "";
    @Input() dialogTitle: string;
    /** The icon of an edit button next to the label, shown only when readonly. Without it, no button shows. */
    @Input() editIcon?: string;

    // Each falls back to a translated default when the caller leaves it out.
    @Input() confirmText?: string;
    @Input() cancelText?: string;
    @Input() emptyText?: string;

    /** Emits the whole record when the user confirms the dialog, never while they are picking. */
    @Output() valueChange: EventEmitter<Record<string, string[]>> = new EventEmitter<
        Record<string, string[]>
    >();
    @Output() editRequested = new EventEmitter<void>();

    /** What the dialog is editing; seeded from `value` each time it opens. */
    protected draft: Record<string, string[]> = {};

    // The picker inside the dialog is only created while the dialog is open. A required field
    // is claimed by the first validationErrors directive that asks for it, and a picker's own
    // directive sits in a deeper view whose hooks run first. Created only on demand, it asks
    // after the trigger has claimed the field and is refused as a nested field's.
    protected dialogOpen = false;

    /** The names on the avatar group, held as one field so the OnPush group isn't handed a
     *  fresh array on every change detection run. */
    protected selectedNames: string[] = [];

    // The picked users live in this component, so the required validator watches them here,
    // and an array is replaced rather than mutated so the signal sees the change.
    private pickedValue = signal<string[]>([]);
    readonly validationValue = this.pickedValue.asReadonly();

    // The avatars open the dialog that fills the field, so the blur that follows means nothing;
    // the dialog closing, however it closes, is the end of the interaction.
    readonly validationTouched = new Subject<void>();

    private _dialogService = inject(CerrixDialogService);

    ngOnChanges(changes: SimpleChanges): void {
        if (changes["value"] || changes["types"]) {
            this.commit(this.value);
        }
    }

    protected get dialogName(): string {
        return `${this.name}-dialog`;
    }

    public openDialog(): void {
        this.draft = Object.fromEntries(
            this.types.map((type) => [type.key, [...(this.value?.[type.key] ?? [])]]),
        );
        this.dialogOpen = true;
        this._dialogService.openDialog(this.dialogName);
    }

    protected onDialogClosed(): void {
        this.dialogOpen = false;
        this.validationTouched.next();
    }

    protected cancel(): void {
        this._dialogService.closeDialog(this.dialogName);
    }

    protected confirm(): void {
        const selection = Object.fromEntries(
            Object.entries(this.draft).map(([key, ids]) => [key, [...ids]]),
        );

        // Closing the dialog below validates straight away, before the new value can flow back
        // in through the caller's binding, so the validator is handed the pick here.
        this.value = selection;
        this.commit(selection);
        this.valueChange.emit(selection);
        this._dialogService.closeDialog(this.dialogName);
    }

    private commit(value: Record<string, string[]>): void {
        const seen = new Set<string>();
        const names: string[] = [];

        // One avatar per user, in type order, however many types the user was picked for.
        for (const type of this.types) {
            for (const id of value?.[type.key] ?? []) {
                const user = seen.has(id) ? undefined : type.users.find((u) => u.Guid === id);
                if (user) {
                    seen.add(id);
                    names.push(user.Name);
                }
            }
        }

        this.selectedNames = names;
        this.pickedValue.set([...new Set(this.types.flatMap((type) => value?.[type.key] ?? []))]);
    }
}
